/*
  File: src/pages/ProductDetail.jsx
  Purpose: Product detail page with image, variant selection, add-to-cart, and recommendations.
  Responsibilities:
  - Fetch product by id and normalize fields for internal components.
  - Derive default color/size; enforce in-stock selection for Add to Cart.
  - Show recommendations carousel and navigate to related items.
*/
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Loader from '../components/common/Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import ProductImageGallery from '../components/product/ImagePanel';
import VariantSelector from '../components/product/VariantSelector';
import RecommendationsCarousel from '../components/product/RecommendationsCarousel';
import { apiClient } from '../auth/api';
import { useCart } from '../cart/CartContext';
import { AlertTriangle } from 'react-feather';
import useVariantSelection from '../hooks/useVariantSelection';

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Use passed navigation state for variant preselection, fallback to null/''
  const initialVariantIdFromState = location.state?.initialVariantId || null;
  const initialColorFromState = location.state?.preferredColor || '';
  const characterFromState = location.state?.character || null;
  const playerFromState = location.state?.player || null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  // Use our unified variant selection hook
  const variantSelection = useVariantSelection(variants, initialVariantIdFromState, initialColorFromState);
  const { selectedVariant, selectedColor, setSelectedColor, selectedSize, setSelectedSize } = variantSelection;
  const { addItem } = useCart();
  const [recs, setRecs] = useState([]);
  const scrollRef = useRef(null);
  const [addedMsg, setAddedMsg] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await apiClient.get(`/products/${id}`);
        const p = data.items ? data.items[0] : data;
        const normalized = {
          id: p.id,
          name: p.name,
          vendorId: p.vendor_id,
          price: (p.price_cents ?? 0),
          currency: p.currency || 'USD',
          imageUrl: p.image_url || null,
          description: p.display_name || '',
          character: p.character || (Array.isArray(p.character_links) && p.character_links.length > 0 ? p.character_links[0] : null),
          player: p.player || (Array.isArray(p.player_links) && p.player_links.length > 0 ? p.player_links[0] : null),
        };
        if (!cancelled) setProduct(normalized);
        if (!cancelled) setVariants(Array.isArray(p.variants) ? p.variants : []);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]); // depend only on id

  // Sync selectedSize to match selectedColor if invalid
  useEffect(() => {
    // whenever selectedColor changes, make sure selectedSize is still valid.
    const availableVariants = variants.filter(v => selectedColor ? v.color === selectedColor : true);
    if (availableVariants.length > 0) {
      const hasSelected = availableVariants.some(v => String(v.size ?? '') === String(selectedSize));
      if (!hasSelected) {
        const firstInStock = availableVariants.find(v => (v.stock ?? 0) > 0) || availableVariants[0];
        setSelectedSize(firstInStock ? String(firstInStock.size ?? '') : '');
      }
    }
  }, [selectedColor, variants, selectedSize, setSelectedSize]);

  // Helper to get all images for the gallery
  const getGalleryImages = () => {
    let variant = null;
    if (selectedColor) {
      variant = variants.find(v => v.color === selectedColor && (!selectedSize || String(v.size ?? '') === String(selectedSize)));
      if (!variant) variant = variants.find(v => v.color === selectedColor);
    }
    if (!variant && variants.length > 0) {
      variant = variants[0];
    }
    if (variant && Array.isArray(variant.images) && variant.images.length > 0) {
      return variant.images;
    }
    // fallback to main product image
    if (product?.imageUrl) {
      return [{ image_url: product.imageUrl, is_primary: true, view_type: 'main' }];
    }
    return [];
  };

  // Helper to get selected variant (color+size)
  const getSelectedVariant = () => {
    const exact = variants.find(v => (
      (selectedColor ? v.color === selectedColor : true) &&
      (selectedSize ? String(v.size ?? '') === String(selectedSize) : true)
    ));
    return exact || null;
  };

  // Helper to get a single primary image url for the selected variant
  const getSelectedVariantImageUrl = () => {
    const imgs = getGalleryImages();
    if (!Array.isArray(imgs) || imgs.length === 0) return product?.imageUrl || '';
    const primary = imgs.find(x => x.is_primary) || imgs[0];
    return primary?.image_url || product?.imageUrl || '';
  };

  const getSelectedVariantId = () => {
    const match = getSelectedVariant();
    return match?.id || match?.variant_id || null;
  };

  // Helper to format price from cents
  const formatPrice = (cents, currency = 'USD') => `${currency} ${(cents / 100).toFixed(2)}`;

  useEffect(() => {
    if (!product?.vendorId) return;
    let cancelled = false;
    (async () => {
      try {
        // Fetch up to 12 products from the same vendor, excluding this one
        const { data } = await apiClient.get('/products', {
          params: {
            vendor_id: product.vendorId,
            limit: 14,
            expand_colors: 1
          }
        });
        let items = (data.items ? data.items : data) || [];
        items = items.filter(p => (p.product_id ?? p.id) !== product.id);
        items = items.slice(0, 12);
        // Normalize
        const mapped = items.map(p => ({
          id: p.product_id ?? p.id,
          name: p.name,
          displayName: p.display_name || p.name,
          price: p.price_cents ?? 0,
          currency: p.currency || 'USD',
          imageUrl: p.image_url || null,
          color: p.color || null,
        }));
        if (!cancelled) setRecs(mapped);
      } catch {
        if (!cancelled) setRecs([]);
      }
    })();
    return () => { cancelled = true; };
  }, [product?.vendorId, product?.id]);

  return (
    <div className="min-h-screen flex flex-col pt-14 sm:pt-16">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading && <Loader label="Loading product" />}
        {error && <div className="text-red-400">Failed to load product.</div>}
        {product && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <button onClick={() => navigate(-1)} className="mb-2 inline-flex items-center gap-2 text-white/70 hover:text-white order-first md:col-span-2 md:order-first">
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>Back</span>
            </button>
            <ProductImageGallery images={getGalleryImages()} name={product.name} />
            <div>
              <h1 className="text-2xl font-bold">{product.name}</h1>
              
              {/* Character Information */}
              {(product.character || characterFromState) && (
                <div className="mt-3 flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-lg">
                  <img
                    src={(product.character || characterFromState).image_url}
                    alt={(product.character || characterFromState).display_name}
                    className="w-12 h-12 rounded-full border-2 border-white/20 bg-white/10 object-cover flex-shrink-0"
                  />
                  <div>
                    <div className="text-white font-medium">{(product.character || characterFromState).display_name}</div>
                    <div className="text-white/60 text-sm">Featured Character</div>
                  </div>
                </div>
              )}

              {/* Player Information */}
              {(product.player || playerFromState) && (
                <div className="mt-3 flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-lg">
                  <img
                    src={(product.player || playerFromState).image_url}
                    alt={(product.player || playerFromState).display_name}
                    className="w-12 h-12 rounded-full border-2 border-white/20 bg-white/10 object-cover flex-shrink-0"
                  />
                  <div>
                    <div className="text-white font-medium">{(product.player || playerFromState).display_name}</div>
                    <div className="text-white/60 text-sm">Featured Player</div>
                  </div>
                </div>
              )}
              
              <div className="mt-2 text-white/80">{product.description}</div>
              <div className="mt-4 text-xl font-semibold">{
                (() => {
                  const v = getSelectedVariant();
                  const cents = (v && typeof v.price_cents === 'number') ? v.price_cents : product.price;
                  const cur = v?.currency || product.currency;
                  return formatPrice(cents, cur);
                })()
              }</div>

              {product && variants.length === 0 && (
                <div className="bg-yellow-100 text-yellow-800 p-3 rounded">No variants found for this product.</div>
              )}
              <VariantSelector
                variants={variants}
                selectedColor={selectedColor}
                onColorChange={(col) => {
                  setSelectedColor(col);
                  console.log('[Color Change] Color:', col, 'Size:', selectedSize, 'Variant:', selectedVariant);
                }}
                selectedSize={selectedSize}
                onSizeChange={(sz) => {
                  setSelectedSize(sz);
                  console.log('[Size Change] Color:', selectedColor, 'Size:', sz, 'Variant:', selectedVariant);
                }}
              />
              {/* Edge-case warning: selection not found or OOS */}
              {variants.length > 0 && (
                (() => {
                  const v = getSelectedVariant();
                  if (!v)
                    return <div className="mt-2 text-xs text-yellow-300 flex items-center gap-1"><AlertTriangle size={15} />This variant combination does not exist.</div>;
                  if ((v.stock ?? 0) <= 0)
                    return <div className="mt-2 text-xs text-red-400 flex items-center gap-1"><AlertTriangle size={15} />Selected variant is out of stock.</div>;
                  return null;
                })()
              )}

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    if (!selectedVariant || (selectedVariant.stock ?? 0) <= 0) return;
                    addItem({
                      id: product.id,
                      name: product.name,
                      price: selectedVariant.price_cents,
                      currency: selectedVariant.currency || product.currency,
                      imageUrl: getSelectedVariantImageUrl(),
                      size: selectedSize || 'N/A',
                      color: selectedColor || null,
                      variantId: selectedVariant.id, // always get from selectedVariant now
                      qty: 1,
                    });
                    setAddedMsg('Added to cart');
                    setTimeout(() => setAddedMsg(''), 1800);
                    console.log('[Add to Cart] Product:', product.id, product.name, 'Selected:', selectedColor, selectedSize, 'VariantId:', selectedVariant?.id, selectedVariant);
                  }}
                  disabled={!selectedSize || !selectedVariant || ((selectedVariant?.stock ?? 1) <= 0)}
                  className={`px-5 py-3 rounded ${(!selectedSize || !selectedVariant || ((selectedVariant?.stock ?? 1) <= 0)) ? 'btn-disabled cursor-not-allowed' : 'btn-primary'}`}
                >
                  Add to Cart
                </button>
                <button onClick={() => {
                  if (!selectedVariant || (selectedVariant.stock ?? 0) <= 0) return;
                  addItem({
                    id: product.id,
                    name: product.name,
                    price: selectedVariant.price_cents,
                    currency: selectedVariant.currency || product.currency,
                    imageUrl: getSelectedVariantImageUrl(),
                    size: selectedSize,
                    color: selectedColor || null,
                    variantId: selectedVariant.id, // always get from selectedVariant now
                    qty: 1,
                  });
                  navigate('/cart');
                  console.log('[Buy Now] Product:', product.id, product.name, 'Selected:', selectedColor, selectedSize, 'VariantId:', selectedVariant?.id, selectedVariant);
                }} className="btn-ghost px-5 py-3 rounded">Buy Now</button>
              </div>
            </div>
          </div>
        )}

        {recs.length > 0 && (
          <div className="mt-10">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold">You may also like</h2>
            </div>
            <RecommendationsCarousel
              items={recs}
              onItemClick={(r) => navigate(`/store/${r.id}${r.color ? `?color=${encodeURIComponent(r.color)}` : ''}`)}
              onView={(r) => {
                if (r.id === product.id) {
                  // Change variant/color if same product but different variant.
                  if (r.color) setSelectedColor(r.color);
                  // Optionally: setSelectedSize if present in r ?
                } else {
                  navigate(`/store/${r.id}${r.color ? `?color=${encodeURIComponent(r.color)}` : ''}`);
                }
              }}
            />
          </div>
        )}
      </main>
      {addedMsg && (
        <div className="fixed inset-0 pointer-events-none flex items-start justify-center pt-24 z-50">
          <div className="pointer-events-auto px-4 py-2 rounded bg-green-600 text-white text-sm shadow-lg border border-white/20">
            {addedMsg}
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}

export default ProductDetail;

